<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import Badge from '@/components/ui/badge/Badge.vue'
import RankIcon from '@/assets/icons/rank.svg'
import RankProgress from '@/components/ui/RankProgress.vue'
import ScoreWidgetMobile from '@/components/score/ScoreWidgetMobile.vue'
import ScoreWidgetDesktop from '@/components/score/ScoreWidgetDesktop.vue'
import Actions from '@/components/actions/Actions.vue'
import WrongRecommendation from '@/assets/wrong-recommendation.svg'
import SmallPlayingCard from '../../views/SmallPlayingCard.vue'
import type { Move } from '@/types'
import RankBenefits from '@/components/RankBenefits.vue'
import Typography from '@/components/ui/typography/Typography.vue'
import ModalWrapper from './ModalWrapper.vue'
import { storeToRefs } from 'pinia'
import { useAuth } from '@/stores/auth'
import { getPlayer } from '@/api/getPlayer'
import { useAppMode } from '@/composables/useAppMode'
import CloseIcon from '@/assets/X.svg'
import { unmountChatUIWebComponent } from '@/helpers/integrations'
import type { PlayerStatistic } from '@acepoker/contract'

const { moves, statisticInfo } = defineProps<{
  moves?: Move[]
  statisticInfo?: PlayerStatistic
  open?: boolean
}>()

const emit = defineEmits<{
  cancel: []
  endRound: []
}>()

const LEVEL_SIZE = 1000

const { mode } = useAppMode()
const { player } = storeToRefs(useAuth())

const username = 'Max Moneymaker'
const rank = computed(() => Math.floor(player.value ? player.value.experience / LEVEL_SIZE : 0))
const progress = ref({
  start: 0,
  current: 0,
  previous: 0,
  next: 1
})
const progressPercent = computed(() =>
  Math.min(
    ((progress.value.current - progress.value.start) /
      (progress.value.next - progress.value.start)) *
      100,
    100
  )
)
const progressDelta = computed(() => progress.value.current - progress.value.previous)

onMounted(async () => {
  const updatedPlayer = await getPlayer()
  const playerExperience = player.value ? player.value.experience : 0
  const bottomLimit = Math.floor(playerExperience / LEVEL_SIZE) * LEVEL_SIZE
  progress.value = {
    start: bottomLimit,
    current: updatedPlayer.experience,
    previous: playerExperience,
    next: bottomLimit + LEVEL_SIZE
  }
  player.value = updatedPlayer
})

onUnmounted(() => {
  unmountChatUIWebComponent()
})
</script>

<template>
  <modal-wrapper>
    <template v-if="false">
      <!-- temporary hidden -->
      <div
        class="desktop:pr-[4px] bg-background absolute top-2 right-4 bottom-2 overflow-y-auto rounded-lg p-4"
      >
        <Typography class="mb-[6px] w-full text-base font-semibold">
          {{ $t('Statistic.label.Recommendation') }}
        </Typography>
        <div class="mb-6 min-h-[126px] w-full overflow-x-auto">
          <div class="flex w-full flex-col gap-2">
            <div
              class="bg-dark-grey flex max-w-[218px] flex-col gap-[6px] rounded p-[14px]"
              v-for="index in 20"
              :key="index"
            >
              <div class="flex items-center gap-2">
                <div class="flex gap-1">
                  <small-playing-card value="As" class="min-h-[28px] min-w-5" />
                  <small-playing-card value="Ks" class="min-h-[28px] min-w-5" />
                </div>
                <div class="flex gap-1">
                  <small-playing-card value="Ts" class="min-h-[28px] min-w-5" />
                  <small-playing-card value="9s" class="min-h-[28px] min-w-5" />
                  <small-playing-card value="8s" class="min-h-[28px] min-w-5" />
                </div>
                <div class="text-white">+</div>
                <small-playing-card value="7s" class="min-h-[28px] min-w-5" />
              </div>
              <div class="flex gap-2">
                <wrong-recommendation class="desktop:w-[18px] desktop:h-[18px] h-3 min-w-3" />
                <div class="text-text-helper text-xs font-normal">
                  {{ $t('Statistic.label.MoveResult') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div class="desktop:h-[72%] flex h-2/5 w-2/5 max-w-[646px] flex-col">
      <div class="mb-2 flex w-full cursor-pointer justify-end">
        <close-icon @click="$emit('cancel')" class="h-7 w-7" />
      </div>
      <div
        class="desktop:p-[16px] desktop:pr-[4px] bg-background flex h-full w-full flex-col items-center overflow-y-auto rounded-[16px] px-4 py-10 text-white"
      >
        <Typography
          v-if="mode === 'desktop'"
          class="desktop:mb-[16px] mb-[8px] w-full text-left text-[20px] leading-[30px] font-medium"
        >
          {{ $t('EndSession.label.BasicStatistics') }}
        </Typography>
        <img
          src="@/assets/icons/statistic/avatar.png"
          srcset="@/assets/icons/statistic/avatar_x2.png 2x"
          alt="logo"
          class="desktop:min-w-[74px] desktop:min-h-[74px] desktop:mb-[8px] h-[54px] min-h-[54px] w-[54px] min-w-[54px] rounded-full"
        />
        <Typography color="secondary" class="mb-[8px] text-[16px] leading-[18px]">
          {{ username }}
        </Typography>
        <Badge
          class="bg-highlight desktop:w-fit desktop:py-[5px] desktop:px-[8px] pointer-events-none mb-[20px] flex h-[34px] w-[100px] justify-center rounded text-lg font-semibold"
        >
          <RankIcon class="desktop:w-[19px] mr-[3px] h-[18px] w-[11px]" />
          {{ $t('Statistic.label.Rank') }}
          {{ rank }}
        </Badge>
        <div class="desktop:max-w-[344px] flex w-full flex-col">
          <rank-progress
            v-model="progressPercent"
            class="mb-2.5"
            :delta="progressDelta"
            :current="progress.current"
          />
          <div class="desktop:mb-[16px] mb-6 flex w-full flex-row justify-between">
            <Typography color="secondary">{{ progress.start }}</Typography>
            <Typography color="inactive">{{ $t('Statistic.label.toNextRank') }}</Typography>
            <Typography color="secondary">{{ progress.next }}</Typography>
          </div>
        </div>
        <actions v-if="moves?.length" :items="moves" class="desktop:mb-[16px] mb-6" />
        <ScoreWidgetMobile
          v-if="statisticInfo && mode === 'mobile'"
          :statistic="statisticInfo"
          class="mb-6"
        />
        <ScoreWidgetDesktop
          v-if="statisticInfo && mode === 'desktop'"
          :statistic="statisticInfo"
          class="mb-6"
        />

        <RankBenefits />
        <div class="mb-[7px] w-full text-base font-semibold text-white">
          {{ $t('Statistic.label.Recommendation') }}
        </div>
        <div class="text-secondary mb-3 w-full text-sm font-normal">
          {{ $t('EndSession.label.ImproveSkills') }}
        </div>
        <div
          v-for="index in 3"
          :key="index"
          class="bg-dark-grey mb-1 flex w-full flex-col rounded-sm px-[14px] py-3"
        >
          <div class="text-17 mb-[3.5px] font-medium text-white">
            {{ $t('EndSession.label.PokerTitle') }}
          </div>
          <div class="mb-[7px] flex items-center gap-[6px]">
            <div class="text-secondary text-xs font-normal">
              {{ $t('EndSession.label.Straddle') }}
            </div>
            <div class="h-4 w-px bg-[rgba(255,255,255,0.6)]" />
            <div class="text-secondary text-xs font-normal">8 {{ $t('label.max') }}</div>
            <div class="h-4 w-px bg-[rgba(255,255,255,0.6)]" />
            <div class="text-secondary text-xs font-normal">
              {{ $t('EndSession.label.Blind') }}
            </div>
          </div>
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1">
              <div class="bg-menu-item-bg flex items-center gap-[2px] rounded px-1 py-px">
                <div class="text-secondary text-sm font-normal">
                  {{ $t('EndSession.label.Hands') }}
                </div>
                <div class="text-sm font-semibold text-white">253</div>
              </div>
              <div class="bg-menu-item-bg flex items-center gap-[2px] rounded px-1 py-px">
                <div class="text-secondary text-sm font-normal">
                  {{ $t('EndSession.label.Score') }}
                </div>
                <div class="text-sm font-semibold text-white">648</div>
              </div>
            </div>
            <div class="bg-success flex items-center gap-[2px] rounded px-1 py-px">
              <div class="text-sm font-normal text-white">
                {{ $t('EndSession.label.Qualities') }}
              </div>
              <div class="text-sm font-semibold text-white">86%</div>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-5 flex w-full gap-[10px] px-[10%]">
        <slot name="button-primary" />
        <slot name="button-secondary" />
      </div>
    </div>
  </modal-wrapper>
</template>
